/*
 * Keeps each cart line's stored "Availability" line-item property in sync with
 * the ordered quantity vs the variant's live inventory.
 *
 * The cart templates compute the correct value per line (see
 * snippets/cart-item-availability.liquid) and expose it as
 * data-availability-desired alongside the currently stored
 * data-availability-current. Whenever those differ - typically right after a
 * quantity change on the cart page or in the cart drawer - this script rewrites
 * the line-item property via /cart/change.js and re-renders the cart sections.
 */
(() => {
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const changeUrl = (window.routes && window.routes.cart_change_url) || `${cartUrl}/change.js`;

  let busy = false;
  let pending = false;

  const collectTasks = () => {
    const tasks = [];
    document.querySelectorAll('[data-cart-item-availability]').forEach((el) => {
      const key = el.getAttribute('data-cart-item-key');
      const desired = el.getAttribute('data-availability-desired') || '';
      const current = el.getAttribute('data-availability-current') || '';
      if (key && desired !== current) tasks.push({ key, desired });
    });
    return tasks;
  };

  const swapSection = (sectionId, selectors) => {
    return fetch(`${cartUrl}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        selectors.forEach(({ selector, mode }) => {
          const source = html.querySelector(selector);
          const target = document.querySelector(selector);
          if (!source || !target) return;
          if (mode === 'replace') target.replaceWith(source);
          else target.innerHTML = source.innerHTML;
        });
      })
      .catch(() => {});
  };

  // Only the line-item property changes here - quantities, prices and totals are
  // untouched - so re-rendering the item lists is enough. This also refreshes the
  // data-availability-* attributes, which stops the reconcile loop.
  const refreshCart = () => {
    const jobs = [];
    if (document.querySelector('cart-items')) {
      jobs.push(swapSection('main-cart-items', [{ selector: 'cart-items', mode: 'inner' }]));
    }
    if (document.querySelector('cart-drawer-items')) {
      jobs.push(swapSection('cart-drawer', [{ selector: 'cart-drawer-items', mode: 'replace' }]));
    }
    return Promise.all(jobs);
  };

  const reconcile = () => {
    if (busy) {
      pending = true;
      return;
    }
    const tasks = collectTasks();
    if (!tasks.length) return;

    busy = true;
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) =>
        tasks.reduce(
          (chain, task) =>
            chain.then(() => {
              const line = cart.items.find((item) => item.key === task.key);
              if (!line) return null;
              const properties = Object.assign({}, line.properties);
              if (task.desired) properties.Availability = task.desired;
              else delete properties.Availability;
              return fetch(changeUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: task.key, quantity: line.quantity, properties }),
              });
            }),
          Promise.resolve(),
        ),
      )
      .then(refreshCart)
      .catch(() => {})
      .finally(() => {
        busy = false;
        if (pending) {
          pending = false;
          reconcile();
        }
      });
  };

  const scheduleReconcile = () => setTimeout(reconcile, 0);

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleReconcile);
  }
  document.addEventListener('cart:refresh', scheduleReconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
